import styled, { DefaultTheme } from 'styled-components';

export const Card = styled.div`
    background: ${({ theme }) => theme.colors.componentBackground};
    border: 1px solid ${({ theme }) => theme.colors.antdBorderSecondary};
    border-radius: 12px;
    display: flex;
    flex-direction: column;
`;

export const CardHeader = styled.div`
    display: flex;
    gap: 14px;
    padding: 16px 16px 10px;
    align-items: center;
`;

export const HeaderInfo = styled.div`
    flex: 1;
    min-width: 0;
`;

export const ProductName = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.antdText};
    line-height: 1.3;
`;

export const MetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    padding: 0 16px 12px;
`;

export const EanLabel = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 13px;
    color: ${({ theme }) => theme.colors.lightFont};
`;

export const CategoryLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.accentColor};
    background: ${({ theme }) => theme.colors.colorPrimaryBg};
    padding: 2px 8px;
    border-radius: 20px;
`;

export const AlertStrip = styled.div<{ level: 'danger' | 'warning' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 16px 4px;
    padding: 8px 12px;
    border-radius: 8px;
    font-size: 12px;
    font-weight: 600;
    background: ${({ level }) =>
        level === 'danger' ? '#d43b451a' : '#c469121f'};
    color: ${({ level, theme }) => getStatusTextColor(level, theme)};
`;

export const Divider = styled.div`
    height: 1px;
    background: ${({ theme }) => theme.colors.antdBorderSecondary};
    margin: 8px 16px;
`;

export const FieldsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1px;
    background: ${({ theme }) => theme.colors.antdBorderSecondary};
    border: 1px solid ${({ theme }) => theme.colors.antdBorderSecondary};
    border-radius: 10px;
    overflow: hidden;
    margin: 4px 16px 14px;
`;

export const FieldCell = styled.div<{ emphasis?: boolean }>`
    background: ${({ emphasis, theme }) => {
        const base = theme.colors.componentBackground;
        return emphasis
            ? `linear-gradient(${theme.colors.accentColor}08, ${theme.colors.accentColor}08), ${base}`
            : base;
    }};
    padding: 11px 12px;
`;

export const FieldLabel = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.lightFont};
    margin-bottom: 4px;
`;

export const FieldValue = styled.div<{
    level?: 'danger' | 'warning';
    strong?: boolean;
    accent?: boolean;
}>`
    font-size: ${({ strong }) => (strong ? '18px' : '16px')};
    font-weight: ${({ strong }) => (strong ? 600 : 500)};
    color: ${({ level, accent, theme }) => {
        if (accent) return theme.colors.accentColor;
        if (level === 'danger') return theme.colors.fail;
        if (level === 'warning') return theme.colors.colorWarning;
        return theme.colors.antdText;
    }};
    line-height: 1.1;
`;

export const AppPriceRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

export const AppPriceCaption = styled.div`
    font-size: 11px;
    color: ${({ theme }) => theme.colors.colorTextTertiary};
    margin-top: 2px;
`;

export const MarginChip = styled.span<{
    level: 'success' | 'warning' | 'danger' | 'neutral';
}>`
    font-size: 11px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 20px;
    background: ${({ level, theme }) => {
        if (level === 'neutral') return theme.colors.antdBorderSecondary;
        if (level === 'success') return `${theme.colors.sucess}22`;
        if (level === 'warning') return '#c469121f';
        return theme.colors.colorErrorBg;
    }};
    color: ${({ level, theme }) => {
        if (level === 'neutral') return theme.colors.lightFont;
        return getStatusTextColor(level, theme);
    }};
`;

export const Footer = styled.div`
    margin-top: auto;
    border-top: 1px solid ${({ theme }) => theme.colors.antdBorderSecondary};
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
`;

export const StatusLabel = styled.label`
    display: block;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.lightFont};
    margin-bottom: 3px;
`;

export const STATUS_COLORS = {
    success: { dot: '#33b57f', bg: '#18885d1a' },
    warning: { dot: '#f29a38', bg: '#c469121f' },
    danger: { dot: '#ea5a64', bg: '#d43b451a' },
};

const STATUS_TEXT_COLORS = {
    light: { success: '#136f4c', warning: '#9a5008', danger: '#b82d37' },
    dark: { success: '#4cc994', warning: '#f5a34a', danger: '#ff7a82' },
};

export const getStatusTextColor = (
    level: 'success' | 'warning' | 'danger',
    theme: DefaultTheme,
) => STATUS_TEXT_COLORS[theme.theme === 'dark' ? 'dark' : 'light'][level];

export const StatusChip = styled.span<{
    level: 'success' | 'warning' | 'danger';
}>`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 10px;
    border-radius: 6px;
    background: ${({ level }) => STATUS_COLORS[level].bg};
    color: ${({ level, theme }) => getStatusTextColor(level, theme)};
`;

export const StatusDot = styled.span<{
    level: 'success' | 'warning' | 'danger';
}>`
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: ${({ level }) => STATUS_COLORS[level].dot};
`;

export const EditButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    background: ${({ theme }) => theme.colors.accentColor};
    border: 1px solid ${({ theme }) => theme.colors.accentColor};
    border-radius: 6px;
    height: 40px;
    flex: 1;
    max-width: 160px;
    padding: 0 16px;
    color: ${({ theme }) => theme.colors.colorTextLightSolid};
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        opacity: 0.9;
    }
`;

export const MenuButton = styled.button`
    width: 32px;
    height: 32px;
    border-radius: 7px;
    background: ${({ theme }) => theme.colors.componentBackground};
    border: 1px solid ${({ theme }) => theme.colors.antdBorderSecondary};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${({ theme }) => theme.colors.lightFont};
    cursor: pointer;
    flex-shrink: 0;
`;
